import { useId, useRef, useState } from "preact/hooks";
import { createRoom, errorMessage } from "../../api/client";
import type { GameMode, MapId } from "../../api/types";
import { requireMe } from "../../state/auth";
import { startGame } from "../../state/game";
import { mapName, requireMeta } from "../../state/meta";
import { Button } from "../components/Button";
import { Field, TextInput } from "../components/Field";
import { OptionCard } from "../components/OptionCard";
import { ScreenFrame } from "../components/ScreenFrame";
import { Slider } from "../components/Slider";
import { Toggle } from "../components/Toggle";
import { cx } from "../cx";
import { useArrowNav } from "../hooks/useArrowNav";
import {
  MODE_RULE_KEYS,
  NAME_MAX,
  NAME_MIN,
  PASSWORD_MAX,
  PLAYERS_MAX,
  PLAYERS_MIN,
  PRACTICE_MAP_ID,
  RULE_SPECS,
  buildCreateRequest,
  defaultMaxPlayers,
  describeLobby,
  lobbyHasErrors,
  modeRulesSummary,
  ruleDefault,
  validateLobby,
} from "./rooms/lobbySettings";
import type { LobbyForm, RuleDrafts } from "./rooms/lobbySettings";
import { useAlive } from "./rooms/useAlive";

export function CreateLobby() {
  const catalog = requireMeta();
  const me = requireMe();
  const alive = useAlive();
  const scroll = useRef<HTMLDivElement>(null);
  useArrowNav(scroll);
  const modeLabelId = useId();
  const mapLabelId = useId();
  const advancedId = useId();

  const [name, setName] = useState(() => `${me.user.displayName}'s lobby`);
  const [mode, setMode] = useState<GameMode>(() => catalog.modes[0].mode);
  const [map, setMap] = useState<MapId>(() => (catalog.maps.find((m) => m.id !== PRACTICE_MAP_ID) ?? catalog.maps[0]).id);
  /** Null until the user moves the slider; then it survives mode changes. */
  const [maxEdited, setMaxEdited] = useState<number | null>(null);
  const [password, setPassword] = useState("");
  const [listed, setListed] = useState(true);
  const [drafts, setDrafts] = useState<RuleDrafts>({});
  const [busy, setBusy] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const modeInfo = catalog.modes.find((m) => m.mode === mode) ?? catalog.modes[0];
  const defaults = modeInfo.rules;
  const lobby: LobbyForm = {
    name,
    mode,
    map,
    maxPlayers: maxEdited ?? defaultMaxPlayers(defaults),
    password,
    listed,
    rules: drafts,
  };
  const errors = validateLobby(lobby);
  const invalid = lobbyHasErrors(errors);
  const summary = invalid
    ? "Fix the highlighted fields to continue."
    : describeLobby(lobby, defaults, { mode: modeInfo.name, map: mapName(map) });

  const submit = async () => {
    if (busy || invalid) return;
    setBusy(true);
    setServerError(null);
    try {
      const join = await createRoom(buildCreateRequest(lobby, defaults));
      // The button stays busy until the game screen replaces this one.
      if (alive.current) startGame(join, lobby.password || undefined);
    } catch (e) {
      if (!alive.current) return;
      setBusy(false);
      setServerError(errorMessage(e));
    }
  };

  return (
    <ScreenFrame
      title="Create lobby"
      kicker="Custom game"
      bodyClass="rooms-create__body"
      footer={
        <>
          <p class={cx("rooms-create__summary", invalid && "is-invalid")} title={summary}>
            {summary}
          </p>
          <Button variant="primary" busy={busy} disabled={invalid} onClick={() => void submit()}>
            Create lobby
          </Button>
        </>
      }
    >
      <div class="rooms-create__scroll" ref={scroll}>
        <div class="rooms-create__grid">
          <div class="rooms-create__col">
            <Field label="Lobby name" hint={`${NAME_MIN}-${NAME_MAX} characters`} error={errors.name}>
              {(id) => (
                <TextInput
                  id={id}
                  value={name}
                  onValue={setName}
                  maxLength={NAME_MAX}
                  placeholder="My lobby"
                  autoComplete="off"
                  invalid={errors.name !== undefined}
                  onEnter={() => void submit()}
                />
              )}
            </Field>

            <div class="rooms-create__group">
              <span id={modeLabelId} class="field__label">
                Mode
              </span>
              <div class="rooms-create__cards" role="radiogroup" aria-labelledby={modeLabelId}>
                {catalog.modes.map((m) => (
                  <OptionCard
                    key={m.mode}
                    role="radio"
                    class="rooms-create__mode"
                    selected={m.mode === mode}
                    onClick={() => {
                      if (m.mode === mode) return;
                      setMode(m.mode);
                      setDrafts({});
                    }}
                  >
                    <span class="card__title">{m.name}</span>
                    <span class="card__meta">{modeRulesSummary(m.rules)}</span>
                  </OptionCard>
                ))}
              </div>
            </div>

            <div class="rooms-create__group">
              <span id={mapLabelId} class="field__label">
                Map
              </span>
              <div class="rooms-create__cards" role="radiogroup" aria-labelledby={mapLabelId}>
                {catalog.maps.map((m) => (
                  <OptionCard
                    key={m.id}
                    role="radio"
                    class="rooms-create__map"
                    selected={m.id === map}
                    title={m.description}
                    onClick={() => setMap(m.id)}
                  >
                    <span class="card__title">{m.name}</span>
                    <span class="card__meta rooms-create__map-desc">{m.description}</span>
                  </OptionCard>
                ))}
              </div>
            </div>
          </div>

          <div class="rooms-create__col">
            <div class="rooms-create__tile">
              <Slider
                label="Max players"
                description={`${PLAYERS_MIN}-${PLAYERS_MAX} players`}
                min={PLAYERS_MIN}
                max={PLAYERS_MAX}
                step={1}
                value={lobby.maxPlayers}
                onChange={(value) => setMaxEdited(Math.round(value))}
              />

              <Field
                label="Password (optional)"
                hint="Leave blank for an open lobby"
                error={errors.password}
              >
                {(id) => (
                  <TextInput
                    id={id}
                    type="password"
                    value={password}
                    onValue={setPassword}
                    maxLength={PASSWORD_MAX}
                    autoComplete="new-password"
                    invalid={errors.password !== undefined}
                    onEnter={() => void submit()}
                  />
                )}
              </Field>

              <Toggle
                class="rooms-create__listed"
                label="Listed in the server browser"
                description="Unlisted lobbies stay hidden from other players' lists."
                checked={listed}
                onChange={setListed}
              />
            </div>

            <section class="rooms-create__advanced" aria-labelledby={advancedId}>
              <h2 id={advancedId} class="rooms-create__heading">
                Advanced rules
              </h2>
              <p class="rooms-create__note">Optional. Starts at the mode defaults; only changes are sent.</p>
              <div class="rooms-create__rules">
                {MODE_RULE_KEYS[mode].map((key) => {
                  const spec = RULE_SPECS[key];
                  const fallback = String(ruleDefault(defaults, key));
                  return (
                    <Field
                      key={key}
                      label={spec.label}
                      hint={`Default ${fallback}, range ${spec.min}-${spec.max}`}
                      error={errors.rules[key]}
                    >
                      {(id) => (
                        <TextInput
                          id={id}
                          value={drafts[key] ?? fallback}
                          onValue={(value) => setDrafts((d) => ({ ...d, [key]: value }))}
                          autoComplete="off"
                          invalid={errors.rules[key] !== undefined}
                          onEnter={() => void submit()}
                        />
                      )}
                    </Field>
                  );
                })}
              </div>
            </section>
          </div>
        </div>
      </div>

      <div class="rooms-create__lane" aria-live="polite">
        {serverError !== null && (
          <div class="notice notice--error">
            <span class="rooms-create__notice-text">{serverError}</span>
          </div>
        )}
      </div>
    </ScreenFrame>
  );
}
